import React, { useState } from 'react';
import { useCMS } from '../context/CMSContext';
import Meta from '../components/Meta';
import AnimatedSection from '../components/AnimatedSection';

export default function Kontakt() {
  const { getPage, loading } = useCMS();
  const page = getPage('kontakt');

  // Form states
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  const title = page?.title || "Kontakt | Chr. Nielsen & Søn V/Leif Nielsen";
  const metaDescription = page?.meta_description || "Har du spørgsmål eller kommentarer? Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig.";
  const headline = page?.headline || "Kontakt";
  const contentHtml = page?.content || "";

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.navn.trim()) {
      newErrors.navn = 'Navn er påkrævet';
    }
    if (!formData.telefon.trim()) {
      newErrors.telefon = 'Telefonnummer er påkrævet';
    } else if (!/^[0-9\s+()-]{8,15}$/.test(formData.telefon.trim())) {
      newErrors.telefon = 'Ugyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail er påkrævet';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Ugyldig e-mailadresse';
    }
    if (!formData.beskrivelse.trim()) {
      newErrors.beskrivelse = 'Beskrivelse er påkrævet';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url // Honeypot
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Fejl ved afsendelse af formular:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Meta title={title} description={metaDescription} pageTitle="Kontakt" />

      {/* Page Header */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/c3c69980-e6b4-4d46-bdae-26d6f6795887/chauffoer-arbejde.jpg" 
            alt="Chr. Nielsen & Søn Chauffør" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-transparent to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white uppercase tracking-tight">
            {headline}
          </h1>
          <div className="h-1.5 w-24 bg-brand-red mx-auto mt-6" />
          <p className="mt-6 text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Har du spørgsmål eller kommentarer? Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pt-10 lg:pt-24 pb-24 bg-brand-dark">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            
            {/* Left Column: Info */}
            <div className="lg:col-span-5 space-y-8 text-left">
              <AnimatedSection direction="left" duration={800}>
                <div className="bg-brand-dark-card border border-brand-dark-border rounded-lg p-8 sm:p-10 shadow-xl">
                  <h3 className="text-lg sm:text-2xl font-bold text-white uppercase tracking-wide border-b border-brand-dark-border pb-4 mb-8">
                    Vores Kontaktinformationer
                  </h3>

                  <div className="space-y-6">
                    {/* Address */}
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-full bg-brand-red/10 border border-brand-red/20 flex items-center justify-center text-brand-red text-lg flex-shrink-0">
                        <i className="fa-solid fa-location-dot" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white uppercase tracking-wider text-xs leading-none mb-2">Adresse</h4>
                        <p className="text-sm text-gray-300 leading-relaxed">
                          <strong>Chr. Nielsen & Søn V/Leif Nielsen</strong><br />
                          Jordrupvej 4<br />
                          6600 Vejen
                        </p>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-full bg-brand-red/10 border border-brand-red/20 flex items-center justify-center text-brand-red text-lg flex-shrink-0">
                        <i className="fa-solid fa-phone" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white uppercase tracking-wider text-xs leading-none mb-2">Telefon (Primær)</h4>
                        <a 
                          href="tel:40535984" 
                          className="text-lg font-extrabold text-white hover:text-brand-red transition-colors whitespace-nowrap"
                        >
                          40 53 59 84
                        </a>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-full bg-brand-green/10 border border-brand-green/20 flex items-center justify-center text-brand-green text-lg flex-shrink-0">
                        <i className="fa-solid fa-envelope" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white uppercase tracking-wider text-xs leading-none mb-2">E-mail</h4>
                        <a 
                          href="mailto:info@chrnielsen.dk" 
                          className="text-lg font-extrabold text-brand-green hover:text-brand-green-hover transition-colors whitespace-nowrap"
                        >
                          info@chrnielsen.dk
                        </a>
                      </div>
                    </div>

                    {/* CVR */}
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 text-lg flex-shrink-0">
                        <i className="fa-solid fa-file-invoice" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white uppercase tracking-wider text-xs leading-none mb-2">CVR Nummer</h4>
                        <p className="text-sm text-gray-300 font-mono">
                          19047938
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map Placeholder or Info */}
                <div className="bg-brand-dark-card border border-brand-dark-border rounded-lg p-8 text-left mt-8">
                  <h4 className="font-bold text-white uppercase tracking-wider text-sm mb-3">Placering i Vejen</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Vores lager og administration er strategisk placeret i Vejen, tæt på det jyske motorvejsnet, hvilket giver os optimale betingelser for hurtig distribution i hele Jylland og resten af Danmark.
                  </p>
                </div>
              </AnimatedSection>
            </div>

            {/* Right Column: Form */}
            <div className="lg:col-span-7 text-left">
              <AnimatedSection direction="right" duration={800}>
                <div className="bg-brand-dark-card border border-brand-dark-border rounded-lg p-8 sm:p-10 shadow-xl">
                  <h3 className="text-lg sm:text-2xl font-bold text-white uppercase tracking-wide border-b border-brand-dark-border pb-4 mb-8">
                    Send os en besked
                  </h3>

                {submitStatus === 'success' && (
                  <div className="mb-8 p-6 bg-brand-green/10 border border-brand-green/30 rounded-md text-brand-green flex items-start gap-4">
                    <i className="fa-solid fa-circle-check text-2xl mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold uppercase tracking-wider text-sm">Tak for din besked!</h4>
                      <p className="text-xs text-gray-300 mt-1 leading-relaxed">
                        Vi har modtaget din henvendelse og vil vende tilbage til dig så hurtigt som muligt.
                      </p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-8 p-6 bg-brand-red/10 border border-brand-red/30 rounded-md text-brand-red flex items-start gap-4">
                    <i className="fa-solid fa-circle-exclamation text-2xl mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold uppercase tracking-wider text-sm">Der opstod en fejl</h4>
                      <p className="text-xs text-gray-300 mt-1 leading-relaxed">
                        Kunne ikke sende din besked. Venligst kontroller dine indtastninger eller kontakt os direkte via telefon eller e-mail.
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field (Absolutely hidden from humans) */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Do not fill this field</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  {/* Name field */}
                  <div>
                    <label htmlFor="navn" className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                      Navn <span className="text-brand-red">*</span>
                    </label>
                    <input
                      type="text"
                      id="navn"
                      name="navn"
                      value={formData.navn}
                      onChange={handleChange}
                      className={`w-full bg-brand-dark border ${
                        errors.navn ? 'border-brand-red' : 'border-brand-dark-border focus:border-brand-red'
                      } rounded-md px-4 py-3.5 text-white text-sm focus:outline-none transition-colors`}
                      placeholder="Dit fulde navn"
                    />
                    {errors.navn && (
                      <p className="text-brand-red text-xs mt-1.5 font-medium">{errors.navn}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Phone field */}
                    <div>
                      <label htmlFor="telefon" className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                        Telefonnummer <span className="text-brand-red">*</span>
                      </label>
                      <input
                        type="tel"
                        id="telefon"
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleChange}
                        className={`w-full bg-brand-dark border ${
                          errors.telefon ? 'border-brand-red' : 'border-brand-dark-border focus:border-brand-red'
                        } rounded-md px-4 py-3.5 text-white text-sm focus:outline-none transition-colors`}
                        placeholder="F.eks. 40535984"
                      />
                      {errors.telefon && (
                        <p className="text-brand-red text-xs mt-1.5 font-medium">{errors.telefon}</p>
                      )}
                    </div>

                    {/* Email field */}
                    <div>
                      <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                        E-mail <span className="text-brand-red">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full bg-brand-dark border ${
                          errors.email ? 'border-brand-red' : 'border-brand-dark-border focus:border-brand-red'
                        } rounded-md px-4 py-3.5 text-white text-sm focus:outline-none transition-colors`}
                        placeholder="F.eks. navn@firma.dk"
                      />
                      {errors.email && (
                        <p className="text-brand-red text-xs mt-1.5 font-medium">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  {/* Description field */}
                  <div>
                    <label htmlFor="beskrivelse" className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                      Beskrivelse / Besked <span className="text-brand-red">*</span>
                    </label>
                    <textarea
                      id="beskrivelse"
                      name="beskrivelse"
                      rows="5"
                      value={formData.beskrivelse}
                      onChange={handleChange}
                      className={`w-full bg-brand-dark border ${
                        errors.beskrivelse ? 'border-brand-red' : 'border-brand-dark-border focus:border-brand-red'
                      } rounded-md px-4 py-3.5 text-white text-sm focus:outline-none transition-colors resize-none`}
                      placeholder="Beskriv din opgave eller dit spørgsmål her..."
                    />
                    {errors.beskrivelse && (
                      <p className="text-brand-red text-xs mt-1.5 font-medium">{errors.beskrivelse}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-brand-red hover:bg-brand-red-hover disabled:bg-brand-red/50 text-white rounded-md font-bold text-base tracking-wide transition-colors duration-200 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Sender besked...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        Send besked
                      </>
                    )}
                  </button>
                </form>
              </div>
              </AnimatedSection>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
